import { useDispatch, useSelector } from 'react-redux';
import { setAuthUser, setProfile, setShowSettings, setShowShareCalendar, setPage, incrementOperation, decrementOperation, setOperationError } from '../../Redux/store';
import { supabase } from '../../supabase/client';
import ShareCodeDisplay from '../../components/ShareCodeDisplay';
import { Calendar, User } from 'lucide-react';

/** Account tab — shows profile info + sign out */
function AccountSettings() {
  const profile = useSelector((s) => s.user.profile);
  const dispatch = useDispatch();

  const signOut = async () => {
    await supabase.auth.signOut();
    dispatch(setAuthUser(null));
    dispatch(setProfile(null));
    dispatch(setShowSettings(false));
  };

  const updateRole = async (newRole) => {
    if (!profile?.id) return;

    // Increment global operation counter
    dispatch(incrementOperation('profile'));

    // Optimistic Redux update
    const oldRole = profile.role;
    dispatch(setProfile({ ...profile, role: newRole }));

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', profile.id)
        .select()
        .maybeSingle();

      if (error) {
        // Set error message
        const errorMessage = error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('fetch')
          ? 'Error: No internet connection'
          : 'Error: Failed to save';
        dispatch(setOperationError({ type: 'profile', error: errorMessage }));
        // Revert on error
        dispatch(setProfile({ ...profile, role: oldRole }));
      } else if (data) {
        // Update with fresh data from Supabase
        dispatch(setProfile(data));
      }
    } catch (err) {
      // Set error message
      const errorMessage = err.message?.toLowerCase().includes('network') || err.message?.toLowerCase().includes('fetch')
        ? 'Error: No internet connection'
        : 'Error: Failed to save';
      dispatch(setOperationError({ type: 'profile', error: errorMessage }));
      // Revert on error
      dispatch(setProfile({ ...profile, role: oldRole }));
    } finally {
      // Decrement global operation counter
      dispatch(decrementOperation('profile'));
    }
  };

  return (
    <div className="space-y-4">
      {profile && (
        <>
          {/* Profile Info & Share Code */}
          <div className="rounded-xl border border-border p-4 space-y-4">
            <div className="flex flex-col gap-1">
              <div className="text-sm font-medium truncate">{profile.name || 'No name set'}</div>
              <div className="text-xs text-muted-foreground truncate">{profile.location || 'No location'}</div>
            </div>

            {/* Share Code row moved to its own component */}
            <ShareCodeDisplay />
            
            {/* Role selector */}
            <div className="pt-2 border-t border-border/50">
              <label className="text-[10px] uppercase font-bold tracking-wider text-muted-foreground ml-1 block mb-1.5">My Role</label>
              <select
                value={profile.role || 'ip'}
                onChange={(e) => updateRole(e.target.value)}
                className="w-full rounded-xl border border-border bg-background px-3 h-11 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
              >
                <option value="ip">Intended Parent</option>
                <option value="donor">Donor</option>
              </select>
            </div>

            {/* View Your Profile button */}
            <button
              onClick={() => {
                dispatch(setPage('profile'));
                dispatch(setShowSettings(false));
              }}
              className="flex items-center justify-center gap-2 w-full rounded-full border border-border py-2.5 text-sm font-medium hover:bg-muted transition-colors"
            >
              <User className="h-4 w-4" />
              View Your Profile
            </button>
          </div>
        </>
      )}

      <button
        onClick={signOut}
        className="w-full rounded-full border border-destructive/40 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/5"
      >
        Sign out
      </button>
    </div>
  );
}

export default AccountSettings;
